import { createSlice } from '@reduxjs/toolkit';
import chatsArquivoService from 'src/services/chatsArquivoService';

// ----------------------------------------------------------------------

const initialState = {
  isLoading: false,
  isLoadingChats: false,
  error: false,
  divisoes: [],
  // { [divisaoId]: [chat] } — os cards de cada coluna do board.
  chatsPorDivisao: {},
  // Lista plana, usada pela aba do Chat.js e pelo modo lista do board.
  listaArquivadas: [],
  totalListaArquivadas: 0,
  isLoadingLista: false,
  totalArquivadas: 0,
  permissoes: {
    escopo: 'proprio',
    podeVer: false,
    podeGerenciarDivisoes: false,
    desarquivarAuto: true
  },
  // Desarquivamentos automáticos recentes, para o alerta na tela.
  alertas: []
};

const slice = createSlice({
  name: 'chatArquivo',
  initialState,
  reducers: {
    startLoading(state) {
      state.isLoading = true;
    },

    startLoadingChats(state) {
      state.isLoadingChats = true;
    },

    startLoadingLista(state) {
      state.isLoadingLista = true;
    },

    hasError(state, action) {
      state.isLoading = false;
      state.isLoadingChats = false;
      state.isLoadingLista = false;
      state.error = action.payload;
    },

    getListaSuccess(state, action) {
      state.isLoadingLista = false;
      state.listaArquivadas = action.payload.chats;
      state.totalListaArquivadas = action.payload.total;
    },

    // "Carregar mais": anexa sem duplicar (o socket pode ter inserido no meio).
    pushListaSuccess(state, action) {
      state.isLoadingLista = false;
      const existentes = new Set(state.listaArquivadas.map((c) => `${c._id}`));
      state.listaArquivadas = [
        ...state.listaArquivadas,
        ...action.payload.chats.filter((c) => !existentes.has(`${c._id}`))
      ];
      state.totalListaArquivadas = action.payload.total;
    },

    getDivisoesSuccess(state, action) {
      state.isLoading = false;
      state.divisoes = action.payload.data || [];
      state.totalArquivadas = action.payload.total_arquivadas || 0;
      if (action.payload.permissoes) state.permissoes = action.payload.permissoes;
    },

    getChatsSuccess(state, action) {
      const { divisaoId, chats } = action.payload;
      state.isLoadingChats = false;
      state.chatsPorDivisao[divisaoId] = chats;
    },

    // Snapshot/rollback do board para o update otimista do drag-and-drop.
    persistBoard(state, action) {
      state.chatsPorDivisao = action.payload;
    },

    removerChatDoBoard(state, action) {
      const chatId = action.payload;
      Object.keys(state.chatsPorDivisao).forEach((divisaoId) => {
        state.chatsPorDivisao[divisaoId] = state.chatsPorDivisao[divisaoId].filter(
          (chat) => `${chat._id}` !== `${chatId}`
        );
      });

      // A lista plana também precisa perder o item: sem isso a conversa
      // continua aparecendo na aba/lista depois de sair do arquivo.
      const tamanhoAntes = state.listaArquivadas.length;
      state.listaArquivadas = state.listaArquivadas.filter(
        (chat) => `${chat._id}` !== `${chatId}`
      );
      if (state.listaArquivadas.length < tamanhoAntes) {
        state.totalListaArquivadas = Math.max(0, state.totalListaArquivadas - 1);
      }

      state.totalArquivadas = Math.max(0, state.totalArquivadas - 1);
      state.divisoes = state.divisoes.map((divisao) => {
        const coluna = state.chatsPorDivisao[`${divisao._id}`];
        // `coluna.length || divisao.total` mantinha o contador antigo quando a
        // coluna esvaziava (0 é falsy). Só recalcula colunas já carregadas.
        return coluna ? { ...divisao, total: coluna.length } : divisao;
      });
    },

    setDivisoes(state, action) {
      state.divisoes = action.payload;
    },

    pushAlerta(state, action) {
      // Evita empilhar o mesmo desarquivamento se o socket duplicar o evento.
      const existe = state.alertas.some((a) => a.chat_id === action.payload.chat_id);
      if (!existe) state.alertas = [action.payload, ...state.alertas].slice(0, 5);
    },

    dismissAlerta(state, action) {
      state.alertas = state.alertas.filter((a) => a.chat_id !== action.payload);
    },

    marcarNaoLidoNoBoard(state, action) {
      const chatId = action.payload;
      Object.keys(state.chatsPorDivisao).forEach((divisaoId) => {
        state.chatsPorDivisao[divisaoId] = state.chatsPorDivisao[divisaoId].map((chat) =>
          `${chat._id}` === `${chatId}` ? { ...chat, novaMensagem: 'sim' } : chat
        );
      });
      state.listaArquivadas = state.listaArquivadas.map((chat) =>
        `${chat._id}` === `${chatId}` ? { ...chat, novaMensagem: 'sim' } : chat
      );
    }
  }
});

export default slice.reducer;

export const {
  persistBoard,
  removerChatDoBoard,
  setDivisoes,
  pushAlerta,
  dismissAlerta,
  marcarNaoLidoNoBoard
} = slice.actions;

// ----------------------------------------------------------------------

export function getDivisoes() {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await chatsArquivoService.listarDivisoes();
      dispatch(slice.actions.getDivisoesSuccess(response));
      return response;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return null;
    }
  };
}

export function getChatsArquivados(divisaoId, params = {}) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoadingChats());
    try {
      const response = await chatsArquivoService.listarChats({ divisao_id: divisaoId, ...params });
      dispatch(slice.actions.getChatsSuccess({ divisaoId, chats: response.data || [] }));
      return response;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return null;
    }
  };
}

/**
 * Lista plana das arquivadas. `divisaoId` nulo traz o arquivo inteiro (o backend
 * já restringe às divisões visíveis para o usuário). `offset > 0` anexa.
 */
export function getListaArquivadas({ divisaoId, busca, offset = 0, limit = 30 } = {}) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoadingLista());
    try {
      const params = { limit, offset };
      if (divisaoId) params.divisao_id = divisaoId;
      if (busca && busca.trim()) params.busca = busca.trim();

      const response = await chatsArquivoService.listarChats(params);
      const payload = { chats: response.data || [], total: response.total || 0 };

      dispatch(
        offset > 0
          ? slice.actions.pushListaSuccess(payload)
          : slice.actions.getListaSuccess(payload)
      );
      return response;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return null;
    }
  };
}

/** Carrega os cards de todas as divisões visíveis (board inteiro). */
export function getBoardArquivo(divisoes, params = {}) {
  return async (dispatch) => {
    await Promise.all(
      (divisoes || []).map((divisao) => dispatch(getChatsArquivados(`${divisao._id}`, params)))
    );
  };
}

export function criarDivisao(data) {
  return async (dispatch) => {
    const response = await chatsArquivoService.criarDivisao(data);
    await dispatch(getDivisoes());
    return response;
  };
}

export function editarDivisao(divisaoId, data) {
  return async (dispatch) => {
    const response = await chatsArquivoService.editarDivisao(divisaoId, data);
    await dispatch(getDivisoes());
    return response;
  };
}

export function excluirDivisao(divisaoId, moverPara) {
  return async (dispatch) => {
    const response = await chatsArquivoService.excluirDivisao(divisaoId, moverPara);
    await dispatch(getDivisoes());
    return response;
  };
}

export function reordenarDivisoes(ordem) {
  return async () => chatsArquivoService.reordenarDivisoes(ordem);
}

export function desarquivarChat(chatId) {
  return async (dispatch) => {
    const response = await chatsArquivoService.desarquivar(chatId);
    dispatch(removerChatDoBoard(chatId));
    return response;
  };
}
